import {BrowserModule} from '@angular/platform-browser';
import {ErrorHandler, NgModule} from '@angular/core';
import * as Sentry from '@sentry/angular';
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptorsFromDi
} from '@angular/common/http';

import {AppRoutingModule} from '@/app-routing.module';
import {AppComponent} from './app.component';
import {BlankComponent} from '@pages/blank/blank.component';
import {ReactiveFormsModule} from '@angular/forms';
import {ProfileComponent} from '@pages/profile/profile.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {DashboardComponent} from '@pages/dashboard/dashboard.component';
import {ToastrModule} from 'ngx-toastr';

import {CommonModule, registerLocaleData} from '@angular/common';
import localeEn from '@angular/common/locales/en';
import {MainMenuComponent} from './pages/main-menu/main-menu.component';
import {SubMenuComponent} from './pages/main-menu/sub-menu/sub-menu.component';
import {StoreModule} from '@ngrx/store';
import {authReducer} from './store/auth/reducer';
import {uiReducer} from './store/ui/reducer';
import {ProfabricComponentsModule} from '@profabric/angular-components';
import {SidebarSearchComponent} from './components/sidebar-search/sidebar-search.component';
import {TokenInterceptor} from './token.interceptor';
import {TableModule} from 'primeng/table';
import {SidebarModule} from 'primeng/sidebar';
import {DialogModule} from 'primeng/dialog';
import {ButtonModule} from 'primeng/button';
import {EmailSettingComponent} from '@pages/email-setting/email-setting.component';
import {AddUpdateEmailSettingComponent} from '@pages/email-setting/add-update-email-setting/add-update-email-setting.component';
import {NgOtpInputModule} from 'ng-otp-input';
import {VerifyOTPResetpasswordComponent} from '@modules/verify-otp-resetpassword/verify-otp-resetpassword.component';
import {SendTestMailComponent} from '@pages/email-setting/send-test-mail/send-test-mail.component';
import {AddSupplierComponent} from '@pages/supplier/add-supplier/add-supplier.component';
import {PaginatorModule} from 'primeng/paginator';
import {UsersComponent} from '@pages/users/users.component';
import {AddFarmComponent} from '@pages/farm/add-farm/add-farm.component';
import {AddFarmhouseComponent} from '@pages/farm/add-farmhouse/add-farmhouse.component';
import {AddFarmhousePartitionComponent} from '@pages/farm/add-farmhouse-partition/add-farmhouse-partition.component';
import {FarmComponent} from '@pages/farm/farm/farm.component';
import {AddPlacementComponent} from '@pages/placement/add-placement/add-placement.component';
import {StageNamePipe} from './common/Pipe/placement-stage.pipe';
import {PlacementListComponent} from '@pages/placement/placement-list/placement-list.component';
import {ActiveMrtalityComponent} from '@pages/placement/active-mortality/active-mortality.component';
import {ChicksFeedingProgramComponent} from '@pages/placement/chicks-feeding-program/chicks-feeding-program.component';
import {AverageBodyWeightComponent} from '@pages/placement/average-body-weight/average-body-weight.component';
import {ActiveOdoAverageWeightComponent} from '@pages/placement/active-odo-average-weight/active-odo-average-weight.component';
import {ActiveVaccinesProgramComponent} from '@pages/placement/active-vaccines-program/active-vaccines-program.component';
import {ActiveMedicationProgramComponent} from '@pages/placement/active-medication-program/active-medication-program.component';
import {AddMedicationComponent} from '@pages/medication/add-medication/add-medication.component';
import {MedicationListComponent} from '@pages/medication/medication-list/medication-list.component';
import {AddDisinfectionComponent} from '@pages/disinfection/add-disinfection/add-disinfection.component';
import {DisinfectionListComponent} from '@pages/disinfection/disinfection-list/disinfection-list.component';
import {ActiveDisinfectionProgramComponent} from '@pages/placement/active-disinfection-program/active-disinfection-program.component';
import {NotificationsListComponent} from '@pages/notifications-list/notifications-list.component';
import {MultiSelectModule} from 'primeng/multiselect';
import {ProfileDashboardComponent} from '@pages/profile-dashboard/profile-dashboard.component';
import {UpdateProfileComponent} from '@pages/profile-dashboard/update-profile/update-profile.component';
import {OverlayPanelModule} from 'primeng/overlaypanel';
import {EditDisinfectionComponent} from '@pages/disinfection/edit-disinfection/edit-disinfection.component';
import {LoaderComponent} from '@pages/loader/loader.component';
import {PlacementDetailComponent} from '@pages/placement-detail/placement-detail.component';
import {TabViewModule} from 'primeng/tabview';
import {TooltipModule} from 'primeng/tooltip';
import {UserPermissionComponent} from '@pages/user-permission/user-permission.component';
import {AddFarmUserComponent} from '@pages/farm-user/add-farm-user/add-farm-user.component';
import {EnvironmentalTimeSeriesGraphComponent} from '@components/dashboard/environmental-time-series-graph/environmental-time-series-graph.component';
import {NgxCountriesDropdownModule} from 'ngx-countries-dropdown';
import {UserPermissionListComponent} from '@pages/user-permission/user-permission-list/user-permission-list.component';
import {FarmUserListComponent} from '@pages/farm-user/farm-user-list/farm-user-list.component';
import {ViewFarmUserComponent} from '@pages/farm-user/view-farm-user/view-farm-user.component';
import {EditUserPermissionComponent} from '@pages/user-permission/edit-user-permission/edit-user-permission.component';
import {EditFarmComponent} from '@pages/farm/edit-farm/edit-farm.component';
import {FarmDetailComponent} from '@pages/farm/farm-detail/farm-detail.component';
import {AiInsightTileComponent} from '@components/medication/ai-insight-tile/ai-insight-tile.component';
import {InsightsPanelComponent} from '@components/medication/insights-panel/insights-panel.component';
import {GeneralComponent} from '@components/cycle/general/general.component';
import {InfoTileComponent} from '@components/info-tile/info-tile.component';
import {SearchFilterComponent} from '@components/search-filter/search-filter.component';
import {DisinfectionComponent} from '@components/disinfection/disinfection.component';
import {KeyMetricComponent} from '@components/dashboard/key-metric/key-metric.component';
import {DashboardInsightPanelComponent} from '@components/dashboard/dashboard-insight-panel/dashboard-insight-panel.component';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {GraphItemComponent} from '@components/dashboard/graph-item/graph-item.component';
import {DeviceConfigurationComponent} from '@pages/settings/device-configuration/device-configuration.component';

import { InputSwitchModule } from 'primeng/inputswitch';

import {UserComponent} from './core/header/user/user.component';
import {TaskSidebarComponent} from './core/task-sidebar/task-sidebar.component';
import {CoreModule} from './core/core.module';

import {HasRoleDirective} from './has-role.directive';

import { PurchaseCallbackComponent } from './components/purchase-callback/purchase-callback.component';
import { PowerBiDashboardComponent } from '@pages/powerbi-dashboard/powerbi-dashboard.component';
registerLocaleData(localeEn, 'en-EN');

@NgModule({
    declarations: [
        AppComponent,
        PowerBiDashboardComponent,
        PurchaseCallbackComponent,
        UserPermissionListComponent,
        StageNamePipe,
        ActiveMrtalityComponent,
        ChicksFeedingProgramComponent,
        FarmUserListComponent,
        ViewFarmUserComponent,
        AverageBodyWeightComponent,
        AddFarmUserComponent,
        ActiveOdoAverageWeightComponent,
        ActiveMedicationProgramComponent,
        ActiveVaccinesProgramComponent,
        ActiveDisinfectionProgramComponent,
        AddMedicationComponent,
        NotificationsListComponent,
        MedicationListComponent,
        AddDisinfectionComponent,
        EditDisinfectionComponent,
        DisinfectionListComponent,
        FarmComponent,
        AddFarmComponent,
        EditFarmComponent,
        FarmDetailComponent,
        LoaderComponent,
        ProfileDashboardComponent,
        UpdateProfileComponent,
        AddFarmhouseComponent,
        AddFarmhousePartitionComponent,
        AddPlacementComponent,
        PlacementDetailComponent,
        PlacementListComponent,
        UsersComponent,
        UserPermissionComponent,
        EditUserPermissionComponent,
        AddSupplierComponent,
        SendTestMailComponent,
        VerifyOTPResetpasswordComponent,
        BlankComponent,
        ProfileComponent,
        DashboardComponent,
        UserComponent,
        UserPermissionListComponent,
        MainMenuComponent,
        SubMenuComponent,
        SidebarSearchComponent,
        EmailSettingComponent,
        AddUpdateEmailSettingComponent
    ],
    bootstrap: [AppComponent],
    imports: [
        CoreModule,
        ProfabricComponentsModule,
        CommonModule,
        NgxCountriesDropdownModule,
        NgOtpInputModule,
        MultiSelectModule,
        TooltipModule,
        OverlayPanelModule,
        TableModule,
        PaginatorModule,
        DialogModule,
        TabViewModule,
        SidebarModule,
        ButtonModule,
        BrowserModule,
        StoreModule.forRoot({auth: authReducer, ui: uiReducer}),
        AppRoutingModule,
        ReactiveFormsModule,
        BrowserAnimationsModule,
        ToastrModule.forRoot({
            timeOut: 3000,
            positionClass: 'toast-top-right',
            preventDuplicates: true
        }),
        AiInsightTileComponent,
        InsightsPanelComponent,
        InfoTileComponent,
        SearchFilterComponent,
        DisinfectionComponent,
        GeneralComponent,
        TaskSidebarComponent,
        KeyMetricComponent,
        DashboardInsightPanelComponent,
        GraphItemComponent,
        DeviceConfigurationComponent,
        HasRoleDirective,
        EnvironmentalTimeSeriesGraphComponent,
        InputSwitchModule
    ],
    providers: [
        // provideHttpClient(withInterceptorsFromDi()),
        provideHttpClient(withInterceptorsFromDi()),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true
        },
        {provide: ErrorHandler, useValue: Sentry.createErrorHandler()},
        provideAnimationsAsync()
    ]
})
export class AppModule {}
